import { useCallback, useEffect, useState } from "react";

type FullscreenDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
};

type FullscreenElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
};

function fullscreenElement() {
  const doc = document as FullscreenDocument;
  return doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
}

function canRequestFullscreen() {
  const root = document.documentElement as FullscreenElement;
  return Boolean(root.requestFullscreen || root.webkitRequestFullscreen);
}

async function enterFullscreen() {
  const root = document.documentElement as FullscreenElement;
  if (root.requestFullscreen) {
    await root.requestFullscreen();
    return;
  }
  await root.webkitRequestFullscreen?.();
}

async function leaveFullscreen() {
  const doc = document as FullscreenDocument;
  if (doc.exitFullscreen) {
    await doc.exitFullscreen();
    return;
  }
  await doc.webkitExitFullscreen?.();
}

/** Tracks document fullscreen and hides the control when the browser cannot enter it. */
export function useFullscreen() {
  const [active, setActive] = useState(false);
  const [supported, setSupported] = useState(false);

  useEffect(() => {
    const sync = () => setActive(Boolean(fullscreenElement()));
    setSupported(canRequestFullscreen());
    sync();
    document.addEventListener("fullscreenchange", sync);
    document.addEventListener("webkitfullscreenchange", sync);
    return () => {
      document.removeEventListener("fullscreenchange", sync);
      document.removeEventListener("webkitfullscreenchange", sync);
    };
  }, []);

  const toggle = useCallback(async () => {
    try {
      if (fullscreenElement()) await leaveFullscreen();
      else await enterFullscreen();
    } catch {
      setActive(Boolean(fullscreenElement()));
    }
  }, []);

  return { active, supported, toggle };
}
